import "@fortawesome/fontawesome-free/css/all.min.css"; // Font Awesome icons
import { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { FaTrash } from "react-icons/fa";
import SignatureCanvas from "react-signature-canvas";
import { toast, ToastContainer } from "react-toastify";
import userSignaturesService from "../../../api/userSignatures";
import "./signatureField.css";

const GenerateSignatureField = () => {
  const sigPadRef = useRef();
  const user = JSON.parse(localStorage.getItem("user"));
  const [drawnSignatures, setDrawnSignatures] = useState([]);

  // Fetch signatures from backend on load
  const fetchDrawnSignatures = async () => {
    if (!user?.userId) return;

    try {
      const res = await userSignaturesService.getDrawSignatures(user.userId);

      const signatures = res.data.map((file) => ({
        id: file.filename,
        image: file.signatureData, // Base64 string from backend
      }));

      setDrawnSignatures(signatures);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch drawn signatures.");
    }
  };

  useEffect(() => {
    fetchDrawnSignatures();
  }, []);

  // Canvas setup for retina devices
  useEffect(() => {
    if (!sigPadRef.current) return;

    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const canvas = sigPadRef.current.getCanvas();
    const context = canvas.getContext("2d");

    const imageData = sigPadRef.current.toData(); // optional

    canvas.width = 500 * ratio;
    canvas.height = 200 * ratio;
    canvas.style.width = "500px";
    canvas.style.height = "200px";
    context.scale(ratio, ratio);

    sigPadRef.current.fromData(imageData);
  }, []);

  const clearSignature = () => {
    sigPadRef.current?.clear();
  };

  const handleGenerate = async () => {
    if (sigPadRef.current.isEmpty()) {
      toast.error("Please draw a signature first.");
      return;
    }

    try {
      const signatureDataUrl = sigPadRef.current.toDataURL("image/png");

      await userSignaturesService.saveDrawSignature({
        image: signatureDataUrl,
        userId: user?.userId,
      });

      toast.success("Signature generated successfully!");

      await fetchDrawnSignatures();
      sigPadRef.current.clear();
    } catch (error) {
      if (error.response?.status === 409) {
        toast.info(error.response.data);
      } else {
        toast.error("Failed to save signature.");
      }
    }
  };

  const handleDelete = async () => {
    try {
      await userSignaturesService.softDeleteSignature(user?.userId, "DRAW");

      await fetchDrawnSignatures();

      toast.success("Signature deleted successfully!");
    } catch (error) {
      console.error("Error deleting signature:", error);
      toast.error("Failed to delete signature.");
    }
  };

  return (
    <div>
      <h3 className="signature-title">Draw Your Signature</h3>

      <SignatureCanvas
        ref={sigPadRef}
        penColor="black"
        canvasProps={{
          className: "signature-canvas",
          width: 500,
          height: 200,
        }}
      />

      <div className="clear-button" style={{ marginTop: "10px" }}>
        <Button className="btn btn-secondary" onClick={clearSignature}>
          Clear
        </Button>
        <Button onClick={handleGenerate} style={{ marginLeft: "10px" }}>
          Generate
        </Button>
      </div>

      {drawnSignatures.length > 0 && (
        <div
          style={{
            marginTop: "20px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          {drawnSignatures.map(({ id, image }) => (
            <img
              key={id}
              src={image}
              alt="Drawn Signature"
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData("text/plain", image);
                e.dataTransfer.setData("fieldType", "IMAGE");
              }}
              style={{
                border: "1px solid #ccc",
                maxWidth: "150px",
                height: "50px",
              }}
            />
          ))}

          <Button
            variant="danger"
            onClick={handleDelete}
            style={{ height: "40px" }}
          >
            <FaTrash />
          </Button>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
      />
    </div>
  );
};

export default GenerateSignatureField;
